import { useId } from '@mantine/hooks'
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form'
import { splitPropsBetweenFormInputAndFormItemWrapper } from '../functions'
import type { FormItemWrapperProps } from '../typings'

type FnUseCustomController = <T extends FieldValues>(
  props: UseControllerProps<T> & FormItemWrapperProps
) => {
  formItemProps: FormItemWrapperProps
  // Should be fixed in the future
  // biome-ignore lint/suspicious/noExplicitAny: Type cannot be strictly defined due to external dependency
  inputProps: any
  fieldState: ReturnType<typeof useController<T>>['fieldState']
}

const getUniqueErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  if ('message' in error && typeof error.message === 'string') {
    return error.message
  }
  const errorEntries = Object.entries(error)
  const messagesSet = new Set<string>()

  for (const [_, value] of errorEntries) {
    if (
      value &&
      typeof value === 'object' &&
      'message' in value &&
      typeof value.message === 'string'
    ) {
      messagesSet.add(value.message)
    }
  }
  const uniqueMessages = Array.from(messagesSet)
  return uniqueMessages.length > 0 ? uniqueMessages.join(', ') : undefined
}

export const useCustomController: FnUseCustomController = ({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  ...props
}) => {
  const { formItemWrapperProps, formInputProps } =
    splitPropsBetweenFormInputAndFormItemWrapper(props)

  const { field, fieldState } = useController({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  })
  const controlWithSchema = control as unknown as {
    schema: {
      fields: Record<typeof name, { spec: { optional: boolean } }>
    }
  }

  const recursivelyGetIsItemOptional = (
    // biome-ignore lint/suspicious/noExplicitAny: Generic type parameter requires any
    schema: { fields: Record<string, any> },
    name: string
  ): boolean => {
    if (name.includes('.')) {
      const [first, ...rest] = name.split('.')

      if (schema.fields[first as string].type === 'array') {
        const [_, ...restFromArray] = rest
        return recursivelyGetIsItemOptional(
          schema.fields[first as string].innerType,
          restFromArray.join('.')
        )
      }
      return recursivelyGetIsItemOptional(schema.fields[first as string], rest.join('.'))
    }
    return (schema.fields[name] as { spec: { optional: boolean } })?.spec?.optional ?? false
  }

  const withAsterisk =
    formItemWrapperProps.withAsterisk !== undefined
      ? formItemWrapperProps.withAsterisk
      : !recursivelyGetIsItemOptional(controlWithSchema.schema, name)

  const errorMessage = formItemWrapperProps.errorMessage ?? getUniqueErrorMessage(fieldState.error)

  const id = useId()
  const ariaIds = {
    hintId: formItemWrapperProps.hint ? `${id}-hint` : undefined,
    errorId: errorMessage ? `${id}-error` : undefined,
  }

  return {
    formItemProps: {
      ...formItemWrapperProps,
      errorMessage,
      ariaIds,
      withAsterisk,
    },
    inputProps: {
      ...formInputProps,
      ...field,
      disabled: formInputProps.disabled,
      ...(ariaIds.hintId && { 'aria-describedby': ariaIds.hintId }),
      ...(ariaIds.errorId && { 'aria-errormessage': ariaIds.errorId, 'aria-invalid': true }),
    },
    fieldState,
  }
}
